// Which variant a configuration buys, and what it costs.
//
// This is the question the Design Studio used to answer twice, once for the
// price label and once for the add-to-cart call, with different answers: a 1"
// Standard in a solid color was advertised at $21 and charged $45. Both the
// studio and the Find Your Ferrule ask this module, so a quote and a charge
// cannot disagree.
//
// Needs window.FERAL_CATALOG (assets/feral-catalog.js) and window.KYRO_PRICES
// (snippets/feral-studio-globals.liquid).

window.FERAL_CART = (function () {

  var C = window.FERAL_CATALOG;

  // Cache the promise, not the answer. All four pack sizes resolve at once, so
  // caching the answer let three of them read an empty slot while the first was
  // still fetching and quote the fallback price for a design that has its own
  // product.
  var _products = {};   // handle -> Promise<product JSON | null>

  function fetchProduct(handle) {
    if (!(handle in _products)) {
      _products[handle] = fetch('/products/' + handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });   // offline or 404: custom product is the fallback
    }
    return _products[handle];
  }

  // Pack size is carried by the variant, not the cart quantity, and products
  // spell it differently: an option, a bare title, or a "3 pack" prefix.
  function variantForQty(prod, qty) {
    if (!prod || !prod.variants) return null;
    var match = prod.variants.filter(function (v) {
      return Number(v.option1) === qty ||
             Number(v.option2) === qty ||
             v.title === String(qty) ||
             v.title.toLowerCase().indexOf(qty + ' ') === 0;
    })[0];
    if (match) return match;
    if (prod.variants.length === 1) return prod.variants[0];   // single-variant design product
    return null;
  }

  function priceOf(id) {
    if (!id) return null;
    var p = window.KYRO_PRICES && window.KYRO_PRICES[id];
    return (p == null) ? null : p;
  }

  function handleFor(design) {
    var d = C.DESIGNS.filter(function (x) { return x.id === design; })[0];
    if (!d || !d.shopUrl) return null;
    return d.shopUrl.split('/products/')[1].split('?')[0];
  }

  // state needs { model, design }. Returns { id, price } where price is in
  // cents; id is null when that pack size genuinely is not purchasable.
  function resolveVariant(state, qty) {
    var model = state.model;
    var design = state.design;
    var info = C.MODEL_INFO[model];

    if (info && info.isKyro) {
      var kid = (C.KYRO_VARIANTS[model] || {})[qty] || null;
      return Promise.resolve({ id: kid, price: priceOf(kid) });
    }

    if (model === 'shorty') {
      /* The shorty sells an Outer Diameter axis, which is the whole reason it
         can fit a wood or a driver. SHORTY_CUSTOM_VARIANTS_BY_OD has carried
         those variants for a while and nothing ever read it, so every shorty
         wood went out as the .550 default no matter what the customer
         answered. Custom designs only - the standard shorty product has no OD
         axis, so a catalogue design on a wood still ships .550 and leans on
         the confirm-OD flag on the order. */
      var byOd = (design === 'custom' && state.shortyOd && C.SHORTY_CUSTOM_VARIANTS_BY_OD)
        ? C.SHORTY_CUSTOM_VARIANTS_BY_OD[state.shortyOd]
        : null;
      var map = byOd || (design === 'custom' ? C.SHORTY_CUSTOM_VARIANTS : C.SHORTY_STD_VARIANTS);
      var sid = map[qty] || null;
      return Promise.resolve({ id: sid, price: priceOf(sid) });
    }

    /* 2" Standard, any design. The run costs $46 whatever is printed on it -
       twice the 1" - so a catalogue design at 2" costs us exactly what an
       upload at 2" costs us, and it was being sold at 1" library prices in
       pack sizes we do not make money on (Wyatt 10/09). One product, one
       variant, ten only, $118.

       Above the library tiers deliberately: at 1" the price follows the
       ARTWORK, which is why those tiers exist, and at 2" it follows the RUN.
       Leaving this below them would have let a design with its own product
       keep winning and quietly stay underwater.

       The cart line therefore reads "Your Custom Design Ferrule - 2" Standard"
       even for a catalogue design. The design itself rides on the "Design"
       line item property, which is what Mass Persona prints from, so
       production is unaffected - but if the catalogue name should appear on
       the line, that needs its own 2" product in Shopify and this branch can
       point at it instead. */
    if (model === '2in') {
      var tid = (C.CUSTOM_DESIGN_2IN_VARIANTS || {})[qty] || null;
      return Promise.resolve({ id: tid, price: priceOf(tid) });
    }

    // 1" Standard. Three tiers, in order:
    //   a library design with its own product  -> that product
    //   any other library design               -> the shared Feral Design product
    //   solid colour, logo only, an upload     -> the custom-design product
    //
    // The middle tier used to fall through to custom, which charged $89 a
    // ten-pack for a catalogue design that should cost $59 - on 24 of the 48.
    var isLibrary = design && design !== 'custom';
    /* A design on a SHARED product must be addressed by variant id, never by
       handle. variantForQty below takes the first variant matching a quantity,
       and on the Gradient Ferrule product that is Augusta's for all ten
       colourways - so a handle lookup would put every gradient in the cart as
       Augusta. Skipping the handle sends it to the DESIGN_VARIANTS branch,
       which is explicit per design. The shopUrl is still there for linking. */
    var rec = (C.DESIGNS || []).filter(function (d) { return d.id === design; })[0];
    var handle = (isLibrary && !(rec && rec.sharedProduct)) ? handleFor(design) : null;
    if (handle) {
      return fetchProduct(handle).then(function (prod) {
        var v = variantForQty(prod, qty);
        if (v) return { id: v.id, price: v.price };
        // Its own product exists but does not sell this pack size. Fall back to
        // this design's variant on the shared product, which still carries the
        // right photo - not the generic one, which would show someone else's
        // ferrule in the cart.
        var byD = (C.DESIGN_VARIANTS && C.DESIGN_VARIANTS[design]) || null;
        var fid = (byD && byD[qty]) || C.FERAL_DESIGN_VARIANTS[qty] || null;
        return { id: fid, price: priceOf(fid) };
      });
    }
    if (isLibrary) {
      // Prefer the variant carrying this design's own photo, so the cart and
      // checkout show what was chosen. FERAL_DESIGN_VARIANTS is the fallback
      // for a design added to the catalogue before it has a variant.
      var byDesign = (C.DESIGN_VARIANTS && C.DESIGN_VARIANTS[design]) || null;
      var vid = (byDesign && byDesign[qty]) || C.FERAL_DESIGN_VARIANTS[qty] || null;
      return Promise.resolve({ id: vid, price: priceOf(vid) });
    }

    /* A solid colour prices like a library design, not like an upload
       (Wyatt 10/10). Nothing is printed from a file for a plain colour, so
       charging the custom price for it was charging for work not done: $89 a
       ten where the catalogue is $59.

       A logo is still custom - there IS a file to handle - which is why this
       needs hasLogo and not just the absence of a design. The caller passes
       it; anything that does not (Find Your Ferrule, which has no logo step)
       simply gets the solid-colour price, which is correct there. */
    /* A solid colour or a fade, with nothing to print, is a library design
       rather than a custom job - $59 a ten-pack, not $89.

       The caller has to assert this. The first cut inferred it from a missing
       design and a missing logo, and Find Your Ferrule - which never passed
       either flag - immediately started selling logo builds at the library
       price. Inferring "colour only" from absent fields means every caller
       that forgets a flag underprices silently, which is the one failure
       nobody reports. An unset colorOnly now falls through to the custom
       price below: too much is visible and gets complained about, too little
       is not. */
    if (state.colorOnly === true) {
      var sid2 = C.FERAL_DESIGN_VARIANTS[qty] || null;
      return Promise.resolve({ id: sid2, price: priceOf(sid2) });
    }

    var id = C.CUSTOM_DESIGN_VARIANTS[qty] || null;
    return Promise.resolve({ id: id, price: priceOf(id) });
  }

  /* "3 or 5 or 10" is what a plain join gives and it reads like a stutter once
     more than one size is left. Oxford-less on purpose: these are short
     numbers in a one-line hint, not prose. */
  function orList(items) {
    if (!items.length) return '';
    if (items.length === 1) return String(items[0]);
    return items.slice(0, -1).join(', ') + ' or ' + items[items.length - 1];
  }

  function money(cents) {
    if (cents == null) return '';
    var d = cents / 100;
    return '$' + (d === Math.round(d) ? d : d.toFixed(2));
  }

  return {
    resolveVariant: resolveVariant,
    orList: orList,
    money: money,
    fetchProduct: fetchProduct,
    variantForQty: variantForQty,
  };

})();
